import React, { useState, useEffect, lazy, Suspense } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import heroImage from "@/assets/font_site_vitrine_pro.jpg";
import logoIcon from "@/assets/regard-de-nature-logo.png";
import { BRAND } from "@/config/brand";
import { supabase } from "@/integrations/supabase/client";
import {
  Leaf,
  Shield,
  FileText,
  Users,
  ArrowRight,
  BookOpen,
  Sprout,
  Calendar,
  Clock,
  Home,
  CalendarDays,
  Menu,
  Mail,
  Phone,
  ShoppingBag,
  Heart,
  Sun,
  Gift,
  Eye,
  Share2,
  Newspaper,
  MapPin,
} from "lucide-react";
import { ProNavigation } from "@/components/ProNavigation";
import { LegalNotice } from "@/components/LegalNotice";
import { PrivacyPolicy } from "@/components/PrivacyPolicy";
import { TermsOfService } from "@/components/TermsOfService";
import { EthicsCharter } from "@/components/EthicsCharter";
import { useNewOrders } from "@/hooks/useNewOrders";
import { FileLibrarySection } from "@/components/pro/FileLibrarySection";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

const Dashboard = () => {
  const { newOrdersCount } = useNewOrders();

  return (
    <div className="min-h-screen bg-gradient-hero">
      <ProNavigation />

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroImage} alt="Consultation naturopathie" className="w-full h-full object-cover" />
        </div>

        <div className="container mx-auto px-3 sm:px-4 py-12 sm:py-16 md:py-24 lg:py-32 relative z-10 text-center space-y-6 sm:space-y-8">
          <div className="flex flex-col items-center justify-center space-y-2 sm:space-y-4">
            <img src={logoIcon} alt={BRAND.name.full} className="w-16 h-16 sm:w-24 sm:h-24 md:w-32 md:h-32 drop-shadow-lg" />
            <h1 className="text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-cinzel font-semibold tracking-wide">
              <span className="text-secondary">{BRAND.name.line1}</span>
              <br className="sm:hidden" />
              <span className="text-primary"> {BRAND.name.line2}</span>
            </h1>
            <p className="text-sm sm:text-base md:text-lg font-montserrat font-semibold uppercase tracking-[0.2em]">
              <span className="text-primary">{BRAND.baseline.parts.text1}</span>
              <span className="text-secondary"> {BRAND.baseline.parts.separator} </span>
              <span className="text-primary">{BRAND.baseline.parts.text2}</span>
            </p>
          </div>

          <div className="space-y-4 sm:space-y-5 w-full max-w-6xl mx-auto">
            {/* Première ligne - 4 boutons */}
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-5">
              <Link to="/patients" className="w-full group">
                <div className="relative overflow-hidden rounded-xl bg-card/80 backdrop-blur-sm border border-border/50 p-3 sm:p-4 h-full transition-all duration-300 hover:shadow-card hover:border-primary/30 hover:-translate-y-1">
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="relative flex flex-col items-center gap-2 text-center">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                      <Users className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
                    </div>
                    <span className="text-xs sm:text-sm font-semibold text-foreground">Consultants</span>
                  </div>
                </div>
              </Link>

              <Link to="/appointments" className="w-full group">
                <div className="relative overflow-hidden rounded-xl bg-card/80 backdrop-blur-sm border border-border/50 p-3 sm:p-4 h-full transition-all duration-300 hover:shadow-card hover:border-primary/30 hover:-translate-y-1">
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="relative flex flex-col items-center gap-2 text-center">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                      <Calendar className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
                    </div>
                    <span className="text-xs sm:text-sm font-semibold text-foreground">Rendez-vous</span>
                  </div>
                </div>
              </Link>

              <Link to="/vitality" className="w-full group">
                <div className="relative overflow-hidden rounded-xl bg-card/80 backdrop-blur-sm border border-border/50 p-3 sm:p-4 h-full transition-all duration-300 hover:shadow-card hover:border-secondary/30 hover:-translate-y-1">
                  <div className="absolute inset-0 bg-gradient-to-br from-secondary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="relative flex flex-col items-center gap-2 text-center">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-secondary/10 flex items-center justify-center group-hover:bg-secondary/20 transition-colors">
                      <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-secondary" />
                    </div>
                    <span className="text-xs sm:text-sm font-semibold text-foreground">Bilan vitalité</span>
                  </div>
                </div>
              </Link>

              <Link to="/session-report" className="w-full group">
                <div className="relative overflow-hidden rounded-xl bg-card/80 backdrop-blur-sm border border-border/50 p-3 sm:p-4 h-full transition-all duration-300 hover:shadow-card hover:border-secondary/30 hover:-translate-y-1">
                  <div className="absolute inset-0 bg-gradient-to-br from-secondary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="relative flex flex-col items-center gap-2 text-center">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-secondary/10 flex items-center justify-center group-hover:bg-secondary/20 transition-colors">
                      <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-secondary" />
                    </div>
                    <span className="text-xs sm:text-sm font-semibold text-foreground">Compte rendu</span>
                  </div>
                </div>
              </Link>
            </div>

            {/* Deuxième ligne - 4 boutons */}
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-5">
              <Link to="/locations" className="w-full group">
                <div className="relative overflow-hidden rounded-xl bg-card/80 backdrop-blur-sm border border-border/50 p-3 sm:p-4 h-full transition-all duration-300 hover:shadow-card hover:border-accent/30 hover:-translate-y-1">
                  <div className="absolute inset-0 bg-gradient-to-br from-accent/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="relative flex flex-col items-center gap-2 text-center">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-accent/10 flex items-center justify-center group-hover:bg-accent/20 transition-colors">
                      <MapPin className="w-5 h-5 sm:w-6 sm:h-6 text-accent" />
                    </div>
                    <span className="text-xs sm:text-sm font-semibold text-foreground">Lieux</span>
                  </div>
                </div>
              </Link>

              <Link to="/availability-settings" className="w-full group">
                <div className="relative overflow-hidden rounded-xl bg-card/80 backdrop-blur-sm border border-border/50 p-3 sm:p-4 h-full transition-all duration-300 hover:shadow-card hover:border-primary/30 hover:-translate-y-1">
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="relative flex flex-col items-center gap-2 text-center">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                      <Clock className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
                    </div>
                    <span className="text-xs sm:text-sm font-semibold text-foreground">Horaires</span>
                  </div>
                </div>
              </Link>

              <Link to="/accompaniments-management" className="w-full group">
                <div className="relative overflow-hidden rounded-xl bg-card/80 backdrop-blur-sm border border-border/50 p-3 sm:p-4 h-full transition-all duration-300 hover:shadow-card hover:border-accent/30 hover:-translate-y-1">
                  <div className="absolute inset-0 bg-gradient-to-br from-accent/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="relative flex flex-col items-center gap-2 text-center">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-accent/10 flex items-center justify-center group-hover:bg-accent/20 transition-colors">
                      <Heart className="w-5 h-5 sm:w-6 sm:h-6 text-accent" />
                    </div>
                    <span className="text-xs sm:text-sm font-semibold text-foreground">Accompagnements</span>
                  </div>
                </div>
              </Link>

              <Link to="/events-management" className="w-full group">
                <div className="relative overflow-hidden rounded-xl bg-card/80 backdrop-blur-sm border border-border/50 p-3 sm:p-4 h-full transition-all duration-300 hover:shadow-card hover:border-accent/30 hover:-translate-y-1">
                  <div className="absolute inset-0 bg-gradient-to-br from-accent/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="relative flex flex-col items-center gap-2 text-center">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-accent/10 flex items-center justify-center group-hover:bg-accent/20 transition-colors">
                      <CalendarDays className="w-5 h-5 sm:w-6 sm:h-6 text-accent" />
                    </div>
                    <span className="text-xs sm:text-sm font-semibold text-foreground">Événements</span>
                  </div>
                </div>
              </Link>
            </div>

            {/* Troisième ligne - 4 boutons */}
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-5">
              <Link to="/billing" className="w-full group relative">
                <div className="relative overflow-hidden rounded-xl bg-card/80 backdrop-blur-sm border border-border/50 p-3 sm:p-4 h-full transition-all duration-300 hover:shadow-card hover:border-secondary/30 hover:-translate-y-1">
                  <div className="absolute inset-0 bg-gradient-to-br from-secondary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="relative flex flex-col items-center gap-2 text-center">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-secondary/10 flex items-center justify-center group-hover:bg-secondary/20 transition-colors">
                      <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-secondary" />
                    </div>
                    <span className="text-xs sm:text-sm font-semibold text-foreground">Facturation</span>
                  </div>
                  {newOrdersCount > 0 && (
                    <Badge className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground h-6 min-w-6 flex items-center justify-center rounded-full text-xs animate-pulse">
                      {newOrdersCount}
                    </Badge>
                  )}
                </div>
              </Link>

              <Link to="/products-management" className="w-full group">
                <div className="relative overflow-hidden rounded-xl bg-card/80 backdrop-blur-sm border border-border/50 p-3 sm:p-4 h-full transition-all duration-300 hover:shadow-card hover:border-secondary/30 hover:-translate-y-1">
                  <div className="absolute inset-0 bg-gradient-to-br from-secondary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="relative flex flex-col items-center gap-2 text-center">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-secondary/10 flex items-center justify-center group-hover:bg-secondary/20 transition-colors">
                      <ShoppingBag className="w-5 h-5 sm:w-6 sm:h-6 text-secondary" />
                    </div>
                    <span className="text-xs sm:text-sm font-semibold text-foreground">Boutique</span>
                  </div>
                </div>
              </Link>

              <Link to="/gift-cards" className="w-full group">
                <div className="relative overflow-hidden rounded-xl bg-card/80 backdrop-blur-sm border border-border/50 p-3 sm:p-4 h-full transition-all duration-300 hover:shadow-card hover:border-secondary/30 hover:-translate-y-1">
                  <div className="absolute inset-0 bg-gradient-to-br from-secondary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="relative flex flex-col items-center gap-2 text-center">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-secondary/10 flex items-center justify-center group-hover:bg-secondary/20 transition-colors">
                      <Gift className="w-5 h-5 sm:w-6 sm:h-6 text-secondary" />
                    </div>
                    <span className="text-xs sm:text-sm font-semibold text-foreground">Cartes Cadeaux</span>
                  </div>
                </div>
              </Link>

              <Link to="/seasonal-wellness" className="w-full group">
                <div className="relative overflow-hidden rounded-xl bg-card/80 backdrop-blur-sm border border-border/50 p-3 sm:p-4 h-full transition-all duration-300 hover:shadow-card hover:border-accent/30 hover:-translate-y-1">
                  <div className="absolute inset-0 bg-gradient-to-br from-accent/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="relative flex flex-col items-center gap-2 text-center">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-accent/10 flex items-center justify-center group-hover:bg-accent/20 transition-colors">
                      <Sun className="w-5 h-5 sm:w-6 sm:h-6 text-accent" />
                    </div>
                    <span className="text-xs sm:text-sm font-semibold text-foreground">Bien-être saisonnier</span>
                  </div>
                </div>
              </Link>
            </div>

            {/* Quatrième ligne - 4 boutons */}
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-5">
              <Link to="/iridology" className="w-full group">
                <div className="relative overflow-hidden rounded-xl bg-card/80 backdrop-blur-sm border border-border/50 p-3 sm:p-4 h-full transition-all duration-300 hover:shadow-card hover:border-primary/30 hover:-translate-y-1">
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="relative flex flex-col items-center gap-2 text-center">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                      <Eye className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
                    </div>
                    <span className="text-xs sm:text-sm font-semibold text-foreground">Iridologie</span>
                  </div>
                </div>
              </Link>

              <Link to="/plants" className="w-full group">
                <div className="relative overflow-hidden rounded-xl bg-card/80 backdrop-blur-sm border border-border/50 p-3 sm:p-4 h-full transition-all duration-300 hover:shadow-card hover:border-primary/30 hover:-translate-y-1">
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="relative flex flex-col items-center gap-2 text-center">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                      <Sprout className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
                    </div>
                    <span className="text-xs sm:text-sm font-semibold text-foreground">Plantes médicinales</span>
                  </div>
                </div>
              </Link>

              <Link to="/bach-flowers" className="w-full group">
                <div className="relative overflow-hidden rounded-xl bg-card/80 backdrop-blur-sm border border-border/50 p-3 sm:p-4 h-full transition-all duration-300 hover:shadow-card hover:border-accent/30 hover:-translate-y-1">
                  <div className="absolute inset-0 bg-gradient-to-br from-accent/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="relative flex flex-col items-center gap-2 text-center">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-accent/10 flex items-center justify-center group-hover:bg-accent/20 transition-colors">
                      <Leaf className="w-5 h-5 sm:w-6 sm:h-6 text-accent" />
                    </div>
                    <span className="text-xs sm:text-sm font-semibold text-foreground">Fleurs de Bach</span>
                  </div>
                </div>
              </Link>

              <Link to="/library" className="w-full group">
                <div className="relative overflow-hidden rounded-xl bg-card/80 backdrop-blur-sm border border-border/50 p-3 sm:p-4 h-full transition-all duration-300 hover:shadow-card hover:border-primary/30 hover:-translate-y-1">
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="relative flex flex-col items-center gap-2 text-center">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                      <BookOpen className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
                    </div>
                    <span className="text-xs sm:text-sm font-semibold text-foreground">Bibliothèque</span>
                  </div>
                </div>
              </Link>
            </div>

            {/* Cinquième ligne - 3 boutons */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-5">
              <Link to="/app" className="w-full group">
                <div className="relative overflow-hidden rounded-xl bg-card/80 backdrop-blur-sm border border-border/50 p-3 sm:p-4 h-full transition-all duration-300 hover:shadow-card hover:border-secondary/30 hover:-translate-y-1">
                  <div className="absolute inset-0 bg-gradient-to-br from-secondary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="relative flex flex-col items-center gap-2 text-center">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-secondary/10 flex items-center justify-center group-hover:bg-secondary/20 transition-colors">
                      <ArrowRight className="w-5 h-5 sm:w-6 sm:h-6 text-secondary" />
                    </div>
                    <span className="text-xs sm:text-sm font-semibold text-foreground">Analyse</span>
                  </div>
                </div>
              </Link>

              <Link to="/newsletter-management" className="w-full group">
                <div className="relative overflow-hidden rounded-xl bg-card/80 backdrop-blur-sm border border-border/50 p-3 sm:p-4 h-full transition-all duration-300 hover:shadow-card hover:border-secondary/30 hover:-translate-y-1">
                  <div className="absolute inset-0 bg-gradient-to-br from-secondary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="relative flex flex-col items-center gap-2 text-center">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-secondary/10 flex items-center justify-center group-hover:bg-secondary/20 transition-colors">
                      <Newspaper className="w-5 h-5 sm:w-6 sm:h-6 text-secondary" />
                    </div>
                    <span className="text-xs sm:text-sm font-semibold text-foreground">Newsletter</span>
                  </div>
                </div>
              </Link>

              <Link to="/social-publishing" className="w-full group">
                <div className="relative overflow-hidden rounded-xl bg-card/80 backdrop-blur-sm border border-border/50 p-3 sm:p-4 h-full transition-all duration-300 hover:shadow-card hover:border-accent/30 hover:-translate-y-1">
                  <div className="absolute inset-0 bg-gradient-to-br from-accent/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="relative flex flex-col items-center gap-2 text-center">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-accent/10 flex items-center justify-center group-hover:bg-accent/20 transition-colors">
                      <Share2 className="w-5 h-5 sm:w-6 sm:h-6 text-accent" />
                    </div>
                    <span className="text-xs sm:text-sm font-semibold text-foreground">Publication Réseaux</span>
                  </div>
                </div>
              </Link>
            </div>
          </div>

        </div>
      </section>

      {/* Bibliothèque de fichiers — Section privée Google Drive */}
      <div className="container mx-auto px-3 sm:px-4">
        <FileLibrarySection />
      </div>

      {/* Footer */}
      <footer className="border-t border-border/30 bg-muted/30 py-12 mt-24">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <img src={logoIcon} alt={BRAND.name.full} className="w-10 h-10" />
                <div>
                  <p className="text-lg font-bold text-primary font-cinzel">{BRAND.name.full}</p>
                  <p className="text-xs text-secondary uppercase tracking-wide">{BRAND.baseline.full}</p>
                </div>
              </div>
              <p className="text-sm text-muted-foreground">Solution professionnelle pour naturopathes</p>
            </div>

            <div>
              <h4 className="font-semibold text-primary mb-4">Produit</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <Dialog>
                    <DialogTrigger asChild>
                      <button className="text-muted-foreground hover:text-primary transition-colors text-left">
                        Fonctionnalités
                      </button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle className="text-2xl font-playfair text-primary">Fonctionnalités</DialogTitle>
                        <DialogDescription>Découvrez tous les outils disponibles</DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4 py-4">
                        <div className="space-y-3">
                          <h3 className="font-semibold text-primary">Gestion des consultants</h3>
                          <p className="text-sm text-muted-foreground">
                            Créez et gérez les fiches de vos consultants avec historique complet, allergies, médicaments
                            et bilans de vitalité.
                          </p>
                        </div>
                        <div className="space-y-3">
                          <h3 className="font-semibold text-primary">Analyse intelligente</h3>
                          <p className="text-sm text-muted-foreground">
                            Système d'analyse avancé des symptômes avec recommandations personnalisées basées sur votre
                            bibliothèque de traitements.
                          </p>
                        </div>
                        <div className="space-y-3">
                          <h3 className="font-semibold text-primary">Planification & Facturation</h3>
                          <p className="text-sm text-muted-foreground">
                            Gérez vos rendez-vous, disponibilités et factures en toute simplicité avec export PDF
                            automatisé.
                          </p>
                        </div>
                        <div className="space-y-3">
                          <h3 className="font-semibold text-primary">Bibliothèques documentaires</h3>
                          <p className="text-sm text-muted-foreground">
                            Accédez à une base complète de plantes médicinales, fleurs de Bach et créez vos propres
                            protocoles de soins.
                          </p>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </li>
                <li>
                  <Dialog>
                    <DialogTrigger asChild>
                      <button className="text-muted-foreground hover:text-primary transition-colors text-left">
                        API
                      </button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl">
                      <DialogHeader>
                        <DialogTitle className="text-2xl font-playfair text-primary">API</DialogTitle>
                        <DialogDescription>Intégrations et développements personnalisés</DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4 py-4">
                        <p className="text-sm text-muted-foreground">
                          Des API sont disponibles sur demande pour intégrer {BRAND.name.full} à votre infrastructure
                          existante.
                        </p>
                        <p className="text-sm text-muted-foreground">
                          Contactez-nous pour plus d'informations sur les possibilités d'intégration.
                        </p>
                      </div>
                    </DialogContent>
                  </Dialog>
                </li>
                <li>
                  <Dialog>
                    <DialogTrigger asChild>
                      <button className="text-muted-foreground hover:text-primary transition-colors text-left">
                        Documentation
                      </button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle className="text-2xl font-playfair text-primary">Documentation</DialogTitle>
                        <DialogDescription>Guides d'utilisation et ressources</DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4 py-4">
                        <div className="space-y-3">
                          <h3 className="font-semibold text-primary">Guide de démarrage</h3>
                          <p className="text-sm text-muted-foreground">
                            Découvrez comment utiliser {BRAND.name.full} étape par étape, de la création de votre
                            premier consultant à l'export de vos factures.
                          </p>
                        </div>
                        <div className="space-y-3">
                          <h3 className="font-semibold text-primary">Tutoriels vidéo</h3>
                          <p className="text-sm text-muted-foreground">
                            Accédez à notre bibliothèque de tutoriels vidéo pour maîtriser toutes les fonctionnalités de
                            la plateforme.
                          </p>
                        </div>
                        <div className="space-y-3">
                          <h3 className="font-semibold text-primary">Base de connaissances</h3>
                          <p className="text-sm text-muted-foreground">
                            Consultez notre base documentaire avec articles détaillés sur chaque fonctionnalité et cas
                            d'usage.
                          </p>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </li>
                <li>
                  <Dialog>
                    <DialogTrigger asChild>
                      <button className="text-muted-foreground hover:text-primary transition-colors text-left">
                        Intégrations
                      </button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl">
                      <DialogHeader>
                        <DialogTitle className="text-2xl font-cinzel text-primary">Intégrations</DialogTitle>
                        <DialogDescription>Connectez {BRAND.name.full} à vos outils</DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4 py-4">
                        <p className="text-sm text-muted-foreground">
                          {BRAND.name.full} peut s'intégrer à vos outils de gestion existants : agendas, logiciels de
                          facturation, CRM...
                        </p>
                        <p className="text-sm text-muted-foreground">
                          Contactez notre équipe pour discuter de vos besoins spécifiques d'intégration.
                        </p>
                      </div>
                    </DialogContent>
                  </Dialog>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-primary mb-4">Support</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <Dialog>
                    <DialogTrigger asChild>
                      <button className="text-muted-foreground hover:text-primary transition-colors text-left">
                        Guide utilisateur
                      </button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle className="text-2xl font-playfair text-primary">Guide utilisateur</DialogTitle>
                        <DialogDescription>Tout ce que vous devez savoir</DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4 py-4">
                        <div className="space-y-3">
                          <h3 className="font-semibold text-primary">Premiers pas</h3>
                          <p className="text-sm text-muted-foreground">
                            1. Créez votre premier consultant dans "Fiche Consultants"
                            <br />
                            2. Configurez vos horaires dans "Horaires d'ouverture"
                            <br />
                            3. Ajoutez vos services dans "Facturation"
                            <br />
                            4. Commencez à prendre des rendez-vous
                          </p>
                        </div>
                        <div className="space-y-3">
                          <h3 className="font-semibold text-primary">Gestion quotidienne</h3>
                          <p className="text-sm text-muted-foreground">
                            Consultez le panneau flottant en bas à droite pour voir vos rendez-vous du jour. Utilisez le
                            menu de navigation rapide pour accéder à toutes les fonctionnalités.
                          </p>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </li>
                <li>
                  <Dialog>
                    <DialogTrigger asChild>
                      <button className="text-muted-foreground hover:text-primary transition-colors text-left">
                        FAQ
                      </button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle className="text-2xl font-playfair text-primary">Questions fréquentes</DialogTitle>
                        <DialogDescription>Réponses aux questions les plus courantes</DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4 py-4">
                        <div className="space-y-2">
                          <h3 className="font-semibold text-primary">Comment exporter mes données ?</h3>
                          <p className="text-sm text-muted-foreground">
                            Vous pouvez exporter vos factures en PDF depuis la section Facturation, et vos bilans de
                            vitalité depuis la fiche de chaque consultant.
                          </p>
                        </div>
                        <div className="space-y-2">
                          <h3 className="font-semibold text-primary">Puis-je personnaliser mes protocoles ?</h3>
                          <p className="text-sm text-muted-foreground">
                            Oui, dans "Bibliothèque de traitements", vous pouvez créer vos propres protocoles
                            personnalisés qui seront utilisés dans les recommandations.
                          </p>
                        </div>
                        <div className="space-y-2">
                          <h3 className="font-semibold text-primary">Mes données sont-elles sécurisées ?</h3>
                          <p className="text-sm text-muted-foreground">
                            Oui, toutes les données sont chiffrées et hébergées en conformité avec le RGPD. Vous êtes le
                            seul à avoir accès aux informations de vos consultants.
                          </p>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </li>
                <li>
                  <Dialog>
                    <DialogTrigger asChild>
                      <button className="text-muted-foreground hover:text-primary transition-colors text-left">
                        Contact
                      </button>
                    </DialogTrigger>
                    <DialogContent className="max-w-md">
                      <DialogHeader>
                        <DialogTitle className="text-2xl font-playfair text-primary">Nous contacter</DialogTitle>
                        <DialogDescription>Choisissez votre mode de contact préféré</DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4 py-4">
                        <a
                          href="mailto:contact@regard-de-nature.fr"
                          className="flex items-center gap-4 p-4 rounded-lg border border-border hover:bg-muted/50 transition-colors"
                        >
                          <div className="w-12 h-12 bg-primary-light rounded-lg flex items-center justify-center text-primary flex-shrink-0">
                            <Mail className="w-6 h-6" />
                          </div>
                          <div>
                            <p className="font-semibold text-primary">Email</p>
                            <p className="text-sm text-muted-foreground">contact@regard-de-nature.fr</p>
                          </div>
                        </a>

                        <a
                          href="tel:+33000000000"
                          className="flex items-center gap-4 p-4 rounded-lg border border-border hover:bg-muted/50 transition-colors"
                        >
                          <div className="w-12 h-12 bg-primary-light rounded-lg flex items-center justify-center text-primary flex-shrink-0">
                            <Phone className="w-6 h-6" />
                          </div>
                          <div>
                            <p className="font-semibold text-primary">Téléphone</p>
                            <p className="text-sm text-muted-foreground">À venir</p>
                          </div>
                        </a>
                      </div>
                    </DialogContent>
                  </Dialog>
                </li>
                <li>
                  <Dialog>
                    <DialogTrigger asChild>
                      <button className="text-muted-foreground hover:text-primary transition-colors text-left">
                        Formations
                      </button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl">
                      <DialogHeader>
                        <DialogTitle className="text-2xl font-cinzel text-primary">Formations</DialogTitle>
                        <DialogDescription>Maîtrisez tous les aspects de {BRAND.name.full}</DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4 py-4">
                        <p className="text-sm text-muted-foreground">
                          Nous proposons des sessions de formation personnalisées pour vous aider à tirer le meilleur
                          parti de la plateforme.
                        </p>
                        <p className="text-sm text-muted-foreground">
                          Formations disponibles : prise en main initiale, gestion avancée, optimisation du workflow,
                          personnalisation des protocoles.
                        </p>
                        <p className="text-sm text-muted-foreground">
                          Contactez-nous pour organiser une session adaptée à vos besoins.
                        </p>
                      </div>
                    </DialogContent>
                  </Dialog>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-primary mb-4">Légal</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <TermsOfService />
                </li>
                <li>
                  <PrivacyPolicy />
                </li>
                <li>
                  <Dialog>
                    <DialogTrigger asChild>
                      <button className="text-muted-foreground hover:text-primary transition-colors text-left">
                        RGPD
                      </button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle className="text-2xl font-playfair text-primary">Conformité RGPD</DialogTitle>
                        <DialogDescription>Protection des données personnelles</DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4 py-4 text-sm">
                        <p className="text-muted-foreground">
                          {BRAND.name.full} est conçu dans le respect du Règlement Général sur la Protection des Données
                          (RGPD).
                        </p>
                        <div className="space-y-2">
                          <h3 className="font-semibold text-primary">Vos droits</h3>
                          <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-2">
                            <li>Droit d'accès à vos données</li>
                            <li>Droit de rectification</li>
                            <li>Droit à l'effacement</li>
                            <li>Droit à la portabilité</li>
                            <li>Droit d'opposition</li>
                          </ul>
                        </div>
                        <div className="space-y-2">
                          <h3 className="font-semibold text-primary">Nos engagements</h3>
                          <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-2">
                            <li>Chiffrement de toutes les données</li>
                            <li>Hébergement en France</li>
                            <li>Accès restreint et sécurisé</li>
                            <li>Sauvegarde régulière</li>
                            <li>Conformité aux normes de sécurité</li>
                          </ul>
                        </div>
                        <p className="text-muted-foreground">
                          Pour exercer vos droits, contactez-nous à : contact@regard-de-nature.fr
                        </p>
                      </div>
                    </DialogContent>
                  </Dialog>
                </li>
                <li>
                  <LegalNotice />
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-border/30 pt-8 text-center text-sm text-muted-foreground">
            <p>{BRAND.legal.copyright(new Date().getFullYear())}</p>
            <p className="mt-2 text-xs">
              La naturopathie ne se substitue pas à un traitement médical et ne pose pas de diagnostic.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Dashboard;
